// src/admin/Coupons.js (Final fixed version)
import React, { useEffect, useState } from "react";
import { api } from "../axiosClient";
import moment from "moment";

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    code: "",
    type: "fixed",
    amount: "",
    minAmount: "",
    expiry: "",
    currency: "",
    active: true,
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await api.get("/api/coupons");
        setCoupons(res.data);
      } catch (err) {
        console.error("Error fetching coupons:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCoupons();
  }, [showModal]);

  const handleSave = async () => {
    if (!form.code || !form.amount || !form.expiry || !form.currency) {
      return alert("Missing fields (code, amount, expiry, currency)");
    }

    const payload = {
      code: form.code,
      type: form.type === "percentage" ? "percentage" : "flat",
      amount: parseFloat(form.amount),
      minCartValue: parseFloat(form.minAmount || 0), // ✅ correct key name
      expiry: new Date(form.expiry),
      currency: form.currency,
      active: form.active,
    };

    try {
      if (editId) {
        await api.put(`/api/coupons/${editId}`, payload);
      } else {
        await api.post("/api/coupons", payload);
      }

      // Reset
      setShowModal(false);
      setEditId(null);
      setForm({
        code: "",
        type: "fixed",
        amount: "",
        minAmount: "",
        expiry: "",
        currency: "",
        active: true,
      });
    } catch (err) {
      console.error("❌ Coupon Save Failed:", err.response?.data || err.message);
      alert(`Failed to save coupon: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleEdit = (c) => {
    setForm({
      code: c.code,
      type: c.type,
      amount: c.amount,
      minAmount: c.minCartValue || 0,
      expiry: moment(c.expiry).format("YYYY-MM-DD"),
      currency: c.currency || "",
      active: c.active,
    });
    setEditId(c._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this coupon?")) {
      try {
        await api.delete(`/api/coupons/${id}`);
        setCoupons((prev) => prev.filter((c) => c._id !== id));
      } catch (err) {
        console.error("Failed to delete coupon:", err);
      }
    }
  };

  const toggleStatus = async (id, value) => {
    try {
      await api.put(`/api/coupons/${id}`, { active: value });
      setCoupons((prev) =>
        prev.map((c) => (c._id === id ? { ...c, active: value } : c))
      );
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };

  if (loading) return <p className="p-6">Loading coupons...</p>;

  return (
    <div className="p-6 relative min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold">Manage Coupons</h1>
        <button
          className="bg-[#105132] text-white px-4 py-2 rounded"
          onClick={() => {
            setForm({
              code: "",
              type: "fixed",
              amount: "",
              minAmount: "",
              expiry: "",
              currency: "",
              active: true,
            });
            setEditId(null);
            setShowModal(true);
          }}
        >
          + Add Coupon
        </button>
      </div>

      <table className="min-w-full text-sm border bg-white shadow-md">
        <thead className="bg-[#105132] text-white">
          <tr>
            <th className="p-2">Code</th>
            <th className="p-2">Type</th>
            <th className="p-2">Currency</th>
            <th className="p-2">Amount</th>
            <th className="p-2">Min Cart</th>
            <th className="p-2">Expiry</th>
            <th className="p-2">Active</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {coupons.map((c) => (
            <tr key={c._id} className="border-t hover:bg-gray-50 text-center">
              <td className="p-2 font-semibold">{c.code}</td>
              <td className="p-2">{c.type}</td>
              <td className="p-2">{c.currency}</td>
              <td className="p-2">
                {c.type === "percentage" ? `${c.amount}%` : `${c.currency === "INR" ? "₹" : ""}${c.amount}`}
              </td>
              <td className="p-2">{c.minCartValue || 0}</td>
              <td className="p-2 text-sm text-gray-600">
                {moment(c.expiry).format("DD MMM YYYY")}
              </td>
              <td className="p-2">
                <input
                  type="checkbox"
                  checked={c.active}
                  onChange={(e) => toggleStatus(c._id, e.target.checked)}
                />
              </td>
              <td className="p-2 space-x-2">
                <button onClick={() => handleEdit(c)} className="text-blue-600">
                  Edit
                </button>
                <button onClick={() => handleDelete(c._id)} className="text-red-600">
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
  <div className="fixed inset-0 bg-black/30 z-40">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl p-6 bg-white rounded shadow-md z-50">
      <h2 className="text-xl font-bold mb-6">{editId ? "Edit Coupon" : "Add Coupon"}</h2>

      {/* Landscape Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Column 1 */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Code</label>
            <input
              type="text"
              placeholder="e.g. FLAT100"
              value={form.code || ""}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]"
              disabled={!!editId}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Discount Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]"
            >
              <option value="fixed">Fixed Amount</option>
              <option value="percentage">Percentage</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Amount</label>
            <input
              type="number"
              placeholder="e.g. 100 or 10%"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]"
            />
          </div>
        </div>

        {/* Column 2 */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]"
            >
              <option value="">-- Select Currency --</option>
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="AED">AED (د.إ)</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Min Cart Value</label>
            <input
              type="number"
              placeholder="Optional"
              value={form.minAmount}
              onChange={(e) => setForm({ ...form, minAmount: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Expiry Date</label>
            <input
              type="date"
              value={form.expiry}
              onChange={(e) => setForm({ ...form, expiry: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#105132]"
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between items-center pt-6">
        <button
          onClick={handleSave}
          className="bg-[#105132] hover:bg-[#0e472b] text-white px-6 py-2 rounded-lg shadow"
        >
          Save
        </button>
        <button
          onClick={() => {
            setShowModal(false);
            setEditId(null);
          }}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default Coupons;
